import { useQueryState } from 'nuqs';
import { useEffect, useMemo, useState } from 'react';
import { FilterMap, Filters, Platform } from '~/screens/MyAgency/types';
import { getStringFromFilters } from '~/screens/utils/url';

import { AdvancedFilters } from '../store';

export const useURLFilters = () => {
  const [urlFilter, setUrlFilter] = useQueryState('filters');
  const [activePlatformUrl, setActivePlatformUrl] =
    useQueryState<Platform | null>('audiencePlatform', {
      defaultValue: null,
      parse: (value) => value as Platform | null,
    });

  /** appliedFiltersMap holds a Map() of applied filters */
  const [appliedFiltersMap, setAppliedFiltersMap] = useState<
    Map<string, string>
  >(new Map());

  // Sync URL query with appliedFiltersMap
  useEffect(() => {
    const filters = urlFilter
      ? Buffer.from(urlFilter, 'base64').toString('utf-8').split(',')
      : [];

    const newFilters = new Map<string, string>();
    filters.forEach((filter) => {
      const [platform, filterType, value] = filter.split(':');
      const key = `${platform}:${String(filterType)}`;
      newFilters.set(key, value);
    });

    setAppliedFiltersMap(newFilters);
  }, [urlFilter]);

  // Convert the Map of filters into an array of filter objects
  const appliedFilters: AdvancedFilters['appliedFilters'] = useMemo(() => {
    const result: Filters[] = [];

    appliedFiltersMap.forEach((value, key) => {
      const [platform, filterType] = key.split(':');
      result.push({
        platform: platform as Platform,
        filterType: filterType as keyof FilterMap[Platform],
        value,
      });
    });

    return result;
  }, [appliedFiltersMap]);

  // Get the filter value for the specified platform and filter type
  const getFilter: AdvancedFilters['getFilter'] = <
    P extends Platform,
    K extends keyof FilterMap[P],
  >(
    platform: P,
    filterType: K,
  ) => {
    const key = `${platform}:${String(filterType)}`;
    return appliedFiltersMap.get(key) as FilterMap[P][K] | undefined;
  };

  // Set the filter value for the specified platform and filter type
  const setFilter: AdvancedFilters['setFilter'] = (
    platform,
    filterType,
    value,
  ) => {
    const key = `${platform}:${String(filterType)}`;
    const newFilters = new Map(appliedFiltersMap);
    newFilters.set(key, value as string); // need typecasting :(

    setUrlFilter(getStringFromFilters(newFilters));
    // updateURL(newFilters, router);
    setAppliedFiltersMap(newFilters);
  };

  // Clear filters by platform and/or filter types
  const clearFilters: AdvancedFilters['clearFilters'] = (
    platform,
    filterTypes,
  ) => {
    const newFilters = new Map(appliedFiltersMap);

    if (!platform && !filterTypes) {
      newFilters.clear();
      setActivePlatformUrl(null);
    } else if (platform && !filterTypes) {
      Array.from(newFilters.keys()).forEach((key) => {
        if (key.startsWith(platform)) {
          newFilters.delete(key);
        }
      });
      setActivePlatformUrl(null);
    } else if (platform && filterTypes) {
      filterTypes.forEach((filterType) => {
        const key = `${platform}:${String(filterType)}`;
        newFilters.delete(key);
      });
    }

    setUrlFilter(getStringFromFilters(newFilters));
    setAppliedFiltersMap(newFilters);
  };

  return {
    appliedFiltersMap,
    setAppliedFiltersMap,
    appliedFilters,
    getFilter,
    setFilter,
    clearFilters,
    activePlatformUrl,
    setActivePlatformUrl,
  };
};
